import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Calculator } from "lucide-react";

import { Header } from "@/components/layout/Header";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listPricingPolicies } from "@/lib/api/pricing-policy";
import { listSimulators } from "@/lib/api/simulators";

export const Route = createFileRoute("/_dashboard/pricing-policy")({
  component: PricingPolicyPage,
});

function formatMarge(min: number | null, max: number | null): string {
  if (min != null && max != null) {
    if (min === max) return `${min}%`;
    return `${min}% — ${max}%`;
  }
  if (min != null) return `≥ ${min}%`;
  if (max != null) return `≤ ${max}%`;
  return "—";
}

function PricingPolicyPage() {
  const { data: policies, isLoading } = useQuery({
    queryKey: ["pricing-policies"],
    queryFn: listPricingPolicies,
  });

  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators", "all-for-policy"],
    queryFn: () => listSimulators({ page_size: 100 }),
  });

  const simulatorMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const sim of simulatorsData?.items ?? []) {
      map.set(sim.id, sim.name);
    }
    return map;
  }, [simulatorsData]);

  return (
    <div className="flex flex-col h-full">
      <Header title="Politique tarifaire" />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        <p className="text-sm text-muted-foreground">
          Politique tarifaire appliquée aux offres Logissimo.
        </p>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Offre</TableHead>
                  <TableHead>Taux de marge à appliquer</TableHead>
                  <TableHead>Dérogation à la politique tarifaire</TableHead>
                  <TableHead>Validation DFI</TableHead>
                  <TableHead>Simulateurs liés</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(4)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-28" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-64" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : policies?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <FileText className="h-10 w-10 opacity-50" />
                        <p>Aucune politique tarifaire configurée</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  policies?.map((policy, index) => (
                    <TableRow
                      key={policy.id}
                      className="border-b transition-colors hover:bg-muted/50"
                      style={{
                        animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                      }}
                    >
                      <TableCell className="font-medium">
                        {policy.offre}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">
                          {formatMarge(policy.marge_min, policy.marge_max)}
                        </span>
                      </TableCell>
                      <TableCell>{policy.derogation}</TableCell>
                      <TableCell>
                        {policy.validation_dfi || (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {policy.simulator_ids?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {policy.simulator_ids.map((id) => (
                              <Link
                                key={id}
                                to="/simulators/$id"
                                params={{ id: String(id) }}
                                className="inline-flex"
                              >
                                <Badge
                                  variant="secondary"
                                  className="gap-1 text-xs cursor-pointer hover:bg-accent transition-colors"
                                >
                                  <Calculator className="h-3 w-3" />
                                  {simulatorMap.get(id) ?? `#${id}`}
                                </Badge>
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
